// Cabecera común: wordmark + pill DEMO + avatar del rol (abre el selector).
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { Source } from '@/api/client';
import { C, F, R } from '@/constants/theme';
import { useRole } from '@/context/RoleContext';
import OnaIcon, { type IconName } from './OnaIcon';

export default function Header({ onAvatar, source }: { onAvatar: () => void; source?: Source }) {
  const { role, sesion, simulando } = useRole();
  const live = source === 'live';
  // 'denegado' NO es demo: es «esto no te corresponde». Decirlo, no disfrazarlo.
  const denegado = source === 'denegado';
  /* 🔴 «DEMO» NO ES EL ESTADO POR DEFECTO, es una AFIRMACIÓN. Mientras el brief
     viaja, `source` viene `undefined` y esto caía al `else` final: durante los dos
     o tres segundos de la primera carga, la cabecera anunciaba DEMO con datos que
     iban a ser reales. Patricio: «hasta que carga aparece como demo». En una
     presentación al CEO, ese cartel dos segundos es exactamente lo que no quieres
     — y además es mentira: la app todavía no sabe de dónde vienen los datos.
     Cuatro estados, no tres: no lo sé · en vivo · sin acceso · de ejemplo. */
  const sinSaber = source === undefined;
  // Con sesión, el avatar son TUS iniciales (no las del rol): identidad, no disfraz.
  const iniciales = (sesion?.nombre || '')
    .split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || role.ini;
  return (
    <View style={s.hdr}>
      <Text style={s.brand}>
        <Text style={{ color: C.aqua }}>ona</Text>
        <Text style={{ color: C.ink, fontFamily: F.displayMed }}> pulse</Text>
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {/* En qué área estás: el saludo ya dice tu nombre, así que el rol vive
            aquí. Ámbar cuando estás mirando una portada que no es la tuya. */}
        <View style={[s.rolPill, simulando && { borderColor: 'rgba(245,180,80,0.5)' }]}>
          <OnaIcon name={role.ic as IconName} size={13} color={simulando ? C.warn : C.aquaSoft} />
          <Text style={[s.rolTxt, simulando && { color: C.warn }]} numberOfLines={1}>{role.short}</Text>
        </View>
        <View style={[s.demo, live && { borderColor: 'rgba(30,211,199,0.5)' }, denegado && { borderColor: 'rgba(245,180,80,0.5)' }]}>
          <Text style={[s.demoTxt, live && { color: C.aqua }, denegado && { color: C.warn }]}>
            {sinSaber ? 'CONECTANDO' : live ? '● LIVE' : denegado ? 'SIN ACCESO' : 'DEMO'}
          </Text>
        </View>
        <Pressable style={s.avatar} onPress={onAvatar} hitSlop={8}>
          <Text style={s.avatarTxt}>{iniciales}</Text>
          {simulando ? <View style={s.simDot} /> : null}
        </Pressable>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  hdr: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingTop: 8, paddingBottom: 10 },
  brand: { fontFamily: F.display, fontSize: 22 },
  demo: { borderColor: C.line, borderWidth: 1, borderRadius: R.pill, paddingHorizontal: 9, paddingVertical: 4 },
  demoTxt: { fontFamily: F.ui, fontSize: 9.5, letterSpacing: 1.2, color: C.faint },
  avatar: {
    width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center',
    backgroundColor: C.aqua, shadowColor: C.aqua, shadowOpacity: 0.35, shadowRadius: 14, shadowOffset: { width: 0, height: 4 }, elevation: 6,
  },
  avatarTxt: { fontFamily: F.uiBold, fontSize: 13, color: '#06282E' },
  rolPill: {
    flexDirection: 'row', alignItems: 'center', gap: 5, maxWidth: 118,
    borderWidth: 1, borderColor: 'rgba(140,200,215,0.28)', borderRadius: R.pill,
    paddingHorizontal: 9, paddingVertical: 4,
  },
  rolTxt: { fontFamily: F.uiSemi, fontSize: 10.5, color: C.aquaSoft, letterSpacing: 0.3 },
  // punto ámbar = estás mirando una portada que no es la tuya
  simDot: {
    position: 'absolute', top: -1, right: -1, width: 11, height: 11, borderRadius: 6,
    backgroundColor: C.warn, borderWidth: 2, borderColor: C.bg,
  },
});
